import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Plus, Upload } from "lucide-react";
import { useState } from "react";
import { data, type Schemas } from "../api/client";
import { Page, Section, useApiQuery } from "../app/Page";
import { useBff, useCan, useMe } from "../app/session";
import { useFormat, useT } from "../i18n";
import { Button } from "../ui/Button";
import { Empty, ErrorState } from "../ui/states";
import { Table } from "../ui/Table";
import { ImportAgents } from "./ImportAgents";
import { NewAgentDialog } from "./NewAgentDialog";

const ALL = "\u0000all"; // the filter's "every folder"; "" is "in no folder", as the API has it
const NEW = "\u0000new";
const FOLDER = /^[^/\s](?:[^/]*[^/\s])?$/;

export function AgentsPage() {
  const t = useT();
  const format = useFormat();
  const canCreate = useCan("agents:write");
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [folder, setFolder] = useState(ALL);
  const folders = useApiQuery("agents:folders", (api) => api.GET("/v1/agents:folders"));
  const agents = useApiQuery(`agents:${folder}`, (api) => api.GET("/v1/agents", {
    params: { query: { limit: 100, ...(folder === ALL ? {} : { folder }) } },
  }), { keepPrevious: true });
  const names = (folders.data?.data ?? []).map((f) => f.name);
  const newAgent = canCreate && (
    <div className="flex gap-2">
      <Button variant="secondary" onClick={() => setImporting(true)}>
        <Upload size={16} aria-hidden="true" />
        {t("agents.import.button")}
      </Button>
      <Button onClick={() => setCreating(true)}>
        <Plus size={16} aria-hidden="true" />
        {t("agents.new.button")}
      </Button>
    </div>
  );
  return (
    <Page title={t("nav.agents")} description={t("agents.description")} actions={newAgent}>
      {(names.length > 0 || folder !== ALL) && (
        <label className="flex items-center gap-2 text-sm">
          {t("agents.folder.filter")}
          <select value={folder} onChange={(e) => setFolder(e.target.value)}
            className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-sm">
            <option value={ALL}>{t("agents.folder.all")}</option>
            <option value="">{t("agents.folder.none")}</option>
            {(folders.data?.data ?? []).map((f) => (
              <option key={f.name} value={f.name}>{t("agents.folder.option", { name: f.name, count: f.agents })}</option>
            ))}
          </select>
        </label>
      )}
      <Section query={agents}>
        {(page) =>
          page.data.length === 0 ? (
            folder !== ALL ? <p className="text-sm text-[var(--color-muted)]">{t("agents.folder.empty")}</p> : (
              <Empty title={t("agents.empty")}>
                <p>{t(canCreate ? "agents.emptyBody" : "agents.emptyReadOnly")}</p>
                {newAgent}
              </Empty>
            )
          ) : (
            <Table
              caption={t("nav.agents")}
              rows={page.data}
              rowKey={(a) => a.id}
              columns={[
                {
                  header: t("agents.col.name"),
                  cell: (a) => (
                    <Link to="/agents/$agentId" params={{ agentId: a.id }} className="font-medium text-[var(--color-primary)] underline">
                      {a.name}
                    </Link>
                  ),
                },
                {
                  header: t("agents.col.folder"),
                  cell: (a) => (canCreate ? <MoveTo agent={a} folders={names} /> : a.folder ?? "—"),
                },
                { header: t("agents.col.published"), cell: (a) => a.published_version ?? t("agents.unpublished") },
                { header: t("agents.col.updated"), cell: (a) => format.dateTime(a.updated_at) },
              ]}
            />
          )
        }
      </Section>
      <NewAgentDialog open={creating} onOpenChange={setCreating} />
      <ImportAgents open={importing} onOpenChange={setImporting} />
    </Page>
  );
}

/** Move an agent into a folder, a new one, or out of any (V1.1-EA-T07). */
function MoveTo({ agent, folders }: { agent: Schemas["AgentOut"]; folders: string[] }) {
  const t = useT();
  const { api } = useBff();
  const tenant = useMe().tenant_id;
  const queries = useQueryClient();
  const move = useMutation({
    mutationFn: (to: string | null) => data(api.PATCH("/v1/agents/{agent_id}", {
      params: { path: { agent_id: agent.id } }, body: { folder: to },
    })),
    onSuccess: () => queries.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith("agents") && q.queryKey[1] === tenant }),
  });
  const choose = (value: string) => {
    if (value === NEW) {
      const name = window.prompt(t("agents.folder.newPrompt"))?.trim();
      if (!name) return;
      if (!FOLDER.test(name) || name.length > 64) {
        window.alert(t("agents.folder.invalid"));
        return;
      }
      move.mutate(name);
      return;
    }
    move.mutate(value === "" ? null : value);
  };
  const shown = agent.folder && !folders.includes(agent.folder) ? [...folders, agent.folder] : folders;
  return (
    <span className="inline-flex flex-col gap-1">
      <select aria-label={t("agents.folder.move", { name: agent.name })} value={agent.folder ?? ""} disabled={move.isPending}
        onChange={(e) => choose(e.target.value)}
        className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 text-sm">
        <option value="">{t("agents.folder.none")}</option>
        {shown.map((f) => <option key={f} value={f}>{f}</option>)}
        <option value={NEW}>{t("agents.folder.new")}</option>
      </select>
      {move.error && <ErrorState error={move.error} />}
    </span>
  );
}
